import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { clearProductTimeout, setProductTimeout } from "@/utils/productTimers";
import useFileTrayStore, { FileWithStatus } from "../useFileTrayStore";
import {
  CheckCircle,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Copy,
  LoaderCircle,
  LockKeyhole,
  OctagonX,
  RefreshCw,
  X,
} from "lucide-react";
import clsx from "clsx";
import { BASE_API_URL } from "@/constants";
import fpFetch from "@/services/fpFetch";
import { FileItem } from "@/components/FpFundBrain/Review/FilesListView";
import useOrgId from "@/components/FpAppV4/hooks/useOrgId";
import { create } from "zustand";
import useDriveStore from "@/zustand/useDriveStore";
import { userCanAdministerOrg } from "@/components/FpAppV4/services/userOrgRoles";
import { queryClient } from "@/ReactQuery/reactQueryClient";
import DuplicateUploadModal from "@/components/common/DuplicateUploadModal";

const useUploadFiles = () => {
  const { files, emptyTray } = useFileTrayStore((state) => state);
  return [files, emptyTray] as const;
};

// TODO: Use websockets to receive updated list of progress files as websocket events are sent
const useProcessingFiles = () => {
  // TODO: Use websocket events to receive realtime updates on files actively being progressed
  // For now, we poll the fileReview/list endpoint repeatedly for "real-time" updates.
  const uploadedFiles = useUploadSession((state) => state.uploadedFiles);
  const currentState = useProcessingTrayGlobal((state) => state.state);
  const everUploaded = useFileTrayStore((state) => state.everUploaded);
  const [processingFiles, setProcessingFiles] = useState<FileItem[]>([]);
  const orgId = useOrgId();
  const selectedDriveId = useDriveStore((state) => state.selectedDriveId);
  const location = useLocation();

  // Poll when we're on documents page or RMS pages
  const isOnDocumentsPage = location.pathname.includes("/documents");
  const isOnRMSPage = location.pathname.includes("/rms");
  const shouldPoll = isOnDocumentsPage || isOnRMSPage;

  const pollingEnabled =
    Boolean(orgId) &&
    currentState !== "disabled" &&
    shouldPoll &&
    !(currentState === "session-only" && !everUploaded);

  // For now, we poll the fileReview/list endpoint repeatedly for "real-time"
  // updates (websockets would replace this, per the TODOs above). The query
  // key includes selectedDriveId to force a fresh read on drive switch, even
  // though the fetch itself isn't drive-filtered (that filtering happens
  // client-side below).
  const { data: fetchedProcessingFiles } = useQuery({
    queryKey: ["processingFiles", orgId, selectedDriveId],
    queryFn: () => fetchProcessingFiles(orgId as string),
    enabled: pollingEnabled,
    refetchInterval: pollingEnabled ? 15_000 : false,
  });

  useEffect(() => {
    if (!fetchedProcessingFiles) return;
    setProcessingFiles((oldFiles) => {
      // If a file no longer exists, it had to have been completed.
      // This is a bit hacky but this would go away if we were using websockets.
      const newlyExistingFiles = new Set(
        fetchedProcessingFiles.map((f) => f.taskId)
      );
      const merged = [...fetchedProcessingFiles];
      for (const file of oldFiles) {
        if (!newlyExistingFiles.has(file.taskId)) {
          merged.push({ ...file, status: "Completed" });
        }
      }
      return merged;
    });
  }, [fetchedProcessingFiles]);

  const files = useMemo(() => {
    // Filter files client-side based on selected drive ID
    const filteredProcessingFiles = selectedDriveId
      ? processingFiles.filter((f) => f.driveId === selectedDriveId)
      : processingFiles;

    return filteredProcessingFiles
      .map((f) => {
        let status: "pending" | "uploading" | "completed" | "failed" =
          "pending" as const;
        if (f.status === "Completed") {
          status = "completed" as const;
        }
        if (f.status === "Failed") {
          status = "failed" as const;
        }

        // Only show files uploaded by current user (session-based tracking)
        const inSession = uploadedFiles.has(f.taskId);
        if (!inSession) {
          return null;
        }

        // Exclude locked files
        if (f.locked) {
          return null;
        }

        return {
          status: status,
          error: status === "failed" ? "Failed to process file" : undefined,
          _id: f.taskId,
          blob_id: f.blobId,
          name: f.fileName,
          locked: f.locked,
          driveId: f.driveId,
          //@ts-expect-error I'm lazy
        } satisfies FileWithStatus;
      })
      .filter((x) => x !== null);
  }, [processingFiles, currentState, uploadedFiles, selectedDriveId]);

  const clearFiles = useCallback(() => setProcessingFiles([]), []);

  return [files, clearFiles] as const;
};

// Updated function to fetch all processing files without drive filtering
const fetchProcessingFiles = async (orgId: string) => {
  const url = `${BASE_API_URL}/userData/fileReview/list?organizationId=${orgId}&stages=false`;
  const response = await fpFetch(url);
  return (await response.json()).ingestedFiles as FileItem[];
};

const TrayViewer = () => {
  const location = useLocation();
  const [isTrayVisible, setIsTrayVisible] = useState(false);
  const [hasFiles, setHasFiles] = useState(false);

  // Show on documents page and RMS pages
  const isDocumentsPage = location.pathname.startsWith("/documents");
  const isRMSPage = location.pathname.startsWith("/rms");
  const shouldShowTray = isDocumentsPage || isRMSPage;

  // Check if there are files to show
  const {
    files: uploadFiles,
    duplicateUploads,
    clearDuplicateUploads,
  } = useFileTrayStore((state) => state);
  const [processingFiles] = useProcessingFiles();

  // Update hasFiles when files change
  useEffect(() => {
    const hasUploadFiles = uploadFiles.length > 0;
    const hasProcessingFiles = processingFiles.length > 0;
    const hasAnyFiles = hasUploadFiles || hasProcessingFiles;

    setHasFiles(hasAnyFiles);

    // Auto-show tray when new files are added
    if (hasAnyFiles) {
      setIsTrayVisible(true);
    }
  }, [uploadFiles.length, processingFiles.length, shouldShowTray]);

  const showTray = shouldShowTray && hasFiles && isTrayVisible;

  return (
    <>
      <DuplicateUploadModal
        open={duplicateUploads.length > 0}
        onClose={clearDuplicateUploads}
        duplicates={duplicateUploads}
      />
      {showTray && (
        <div
          className={clsx(
            "fixed bottom-[-1px] right-[20px] z-[9999999999] bg-white overflow-auto",
            "border-t border-l border-r rounded-t-md",
            "min-w-[500px] max-h-[500px]"
          )}
        >
          <UploadTray
            onShowTray={() => setIsTrayVisible(true)}
            onCloseTray={() => setIsTrayVisible(false)}
          />
          <ProcessingTray
            onShowTray={() => setIsTrayVisible(true)}
            onCloseTray={() => setIsTrayVisible(false)}
          />
        </div>
      )}
    </>
  );
};

interface UploadSession {
  uploadedFiles: Set<string>;
  uploadedFilesByEntity: Map<string, Set<string>>; // entityId -> Set of taskIds
  addUploadedFile: (file: string, entityId?: string) => void;
  getUploadedFilesForEntity: (entityId: string) => Set<string>;
}

export const useUploadSession = create<UploadSession>((set, get) => ({
  uploadedFiles: new Set([]),
  uploadedFilesByEntity: new Map(),
  addUploadedFile: (file: string, entityId?: string) => {
    set((state) => {
      const newUploadedFiles = new Set([...state.uploadedFiles, file]);
      const newUploadedFilesByEntity = new Map(state.uploadedFilesByEntity);

      if (entityId) {
        const entityFiles = newUploadedFilesByEntity.get(entityId) || new Set();
        newUploadedFilesByEntity.set(entityId, new Set([...entityFiles, file]));
      }

      return {
        uploadedFiles: newUploadedFiles,
        uploadedFilesByEntity: newUploadedFilesByEntity,
      };
    });
  },
  getUploadedFilesForEntity: (entityId: string) => {
    return get().uploadedFilesByEntity.get(entityId) || new Set();
  },
}));

const UploadTray = ({
  onShowTray,
  onCloseTray,
}: {
  onShowTray?: () => void;
  onCloseTray?: () => void;
}) => {
  const removeFile = useFileTrayStore((state) => state.removeFile);
  const updateFile = useFileTrayStore((state) => state.updateFile);
  const [showUploadTray, setShowUploadTray] = useState(false);
  const [files, clearFiles] = useUploadFiles();
  const orgId = useOrgId();

  // Calculate upload status
  const uploadStatus = useMemo(() => {
    const failedFiles = files.filter((f) => f.status === "failed" || f.error);
    const pendingFiles = files.filter(
      (f) => f.status === "pending" || f.status === "uploading"
    );
    const completedFiles = files.filter(
      (f) => f.status === "completed" && !f.error && !f.duplicate
    );

    return {
      hasFailures: failedFiles.length > 0,
      allComplete: pendingFiles.length === 0,
      failedFiles,
      failedCount: failedFiles.length,
      totalCount: files.length,
    };
  }, [files]);

  // Show this tray as soon as we have files to be uploaded
  useEffect(() => {
    if (!showUploadTray && files.length > 0) {
      setShowUploadTray(true);
      onShowTray?.(); // Notify parent to show the tray
    }
  }, [showUploadTray, files, onShowTray]);

  const handleRetryFailed = useCallback(async () => {
    // Re-import uploadFiles dynamically to avoid circular dependency
    const { default: uploadFiles } = await import("../services/uploadFiles");
    const driveId = useDriveStore.getState().selectedDriveId;

    // Get failed files and reset their status to pending
    const failedFiles = files.filter((f) => f.status === "failed" || f.error);

    // Get the original File objects for retry
    const filesToRetry = failedFiles
      .map((f) => f.originalFile || f)
      .filter((f) => f instanceof File);

    if (filesToRetry.length === 0) {
      console.error("No valid files to retry");
      return;
    }

    failedFiles.forEach((file) => {
      updateFile({
        ...file,
        status: "pending",
        error: undefined,
      } as FileWithStatus);
    });

    // Retry the upload for failed files using original File objects
    await uploadFiles(
      filesToRetry,
      [],
      {
        orgId,
        driveId,
      },
      files
    );

    // Invalidate the file list query to refresh the tree after retry
    queryClient.invalidateQueries({
      queryKey: ["orgFileList"],
    });
  }, [files, updateFile, orgId]);

  if (!showUploadTray) return null;

  // Determine title and color based on upload status
  const title =
    uploadStatus.allComplete && uploadStatus.hasFailures
      ? "Upload Failed"
      : uploadStatus.allComplete
        ? "Upload Succeeded"
        : "Uploading Files";

  const titleColor =
    uploadStatus.allComplete && uploadStatus.hasFailures
      ? "bg-red-600"
      : undefined;

  return (
    <TraySection
      title={title}
      titleColor={titleColor}
      files={files}
      placeholder={
        uploadStatus.allComplete && uploadStatus.hasFailures ? (
          <div className="p-4">
            <p className="text-red-600 font-semibold mb-2">
              {uploadStatus.failedCount} file
              {uploadStatus.failedCount > 1 ? "s" : ""} failed to upload
            </p>
            <button
              onClick={handleRetryFailed}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
            >
              Retry Failed Uploads
            </button>
          </div>
        ) : (
          <p className="p-2">
            Your files have now been captured by our system and are being
            <br />
            processed. It is now safe to navigate away and close this page.
          </p>
        )
      }
      closeTray={() => {
        setShowUploadTray(false);
        clearFiles();
      }}
      removeFile={removeFile}
      onCloseTray={onCloseTray}
      showFailedFiles={true}
      showRetryButton={uploadStatus.allComplete && uploadStatus.hasFailures}
      onRetryFailed={handleRetryFailed}
    />
  );
};

export type ProcessingTrayVisibility = "disabled" | "session-only" | "enabled";
interface ProcessingTrayGlobal {
  state: ProcessingTrayVisibility;
  toggle: (newState: ProcessingTrayVisibility) => void;
}

export const useProcessingTrayGlobal = create<ProcessingTrayGlobal>((set) => ({
  state: "disabled",
  toggle: (newState: ProcessingTrayVisibility) => set({ state: newState }),
}));

const ProcessingTray = ({
  onShowTray,
  onCloseTray,
}: {
  onShowTray?: () => void;
  onCloseTray?: () => void;
}) => {
  const [showProcessingTray, setShowProcessingTray] = useState(false);
  const [files, clearFiles] = useProcessingFiles();
  const userData = useSelector((state: any) => state.userData);
  // Failed files are always shown to the organization's admin (#10718: the
  // admin, not the staff bit).
  const isOrgAdmin = userCanAdministerOrg(userData);

  useEffect(() => {
    if (!showProcessingTray && files.length > 0) {
      setShowProcessingTray(true);
      onShowTray?.(); // Notify parent to show the tray
    }
  }, [showProcessingTray, files, onShowTray]);

  if (!showProcessingTray) return null;
  return (
    <TraySection
      title="Processing Files"
      //@ts-expect-error Lazy
      files={files}
      placeholder={
        <p className="p-2">
          All files have been processed and ingested by our system.
          <br />
          You may now use Finpilot with this new data.
        </p>
      }
      closeTray={() => {
        clearFiles();
        setShowProcessingTray(false);
      }}
      isOrgAdmin={isOrgAdmin}
      onCloseTray={onCloseTray}
    />
  );
};

interface TraySectionProps {
  title: string;
  titleColor?: string;
  files: FileWithStatus[];
  placeholder: React.ReactElement;
  closeTray: () => void;
  removeFile?: (file: FileWithStatus) => void;
  isOrgAdmin?: boolean;
  onCloseTray?: () => void;
  showFailedFiles?: boolean;
  showRetryButton?: boolean;
  onRetryFailed?: () => void;
}

const TraySection = ({
  title,
  titleColor,
  files,
  placeholder,
  closeTray,
  removeFile,
  isOrgAdmin = false,
  onCloseTray,
  showFailedFiles = false,
  showRetryButton = false,
  onRetryFailed,
}: TraySectionProps) => {
  const [collapsed, setCollapsed] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5; // Show 5 files per page

  const shownFiles = useMemo(() => {
    return files.filter((f) => {
      // Always show failed files in UploadTray, only hide them in ProcessingTray for non-superadmins
      if (f.status === "failed" && !showFailedFiles && !isOrgAdmin) {
        return false;
      }
      // Don't show locked files
      if (f.locked) {
        return false;
      }
      return f.duplicate || f.error || f.status !== "completed";
    });
  }, [files, isOrgAdmin, showFailedFiles]);

  const totalPages = Math.ceil(shownFiles.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentFiles = shownFiles.slice(startIndex, endIndex);

  const isCompleted = shownFiles.length === 0;

  // Reset to first page when files change
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(1);
    }
  }, [currentPage, totalPages]);

  useEffect(() => {
    if (isCompleted) {
      const timeout = setProductTimeout(closeTray, 10_000);
      return () => clearProductTimeout(timeout);
    }
  }, [isCompleted]);

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const goToPrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  return (
    <div className="flex flex-col">
      <div
        className={clsx(
          "flex items-center justify-between",
          titleColor || (isCompleted ? "bg-green-600" : "bg-gray-800"),
          "p-2 border-b sticky top-0 z-[10] text-white"
        )}
      >
        <span className="ml-2 flex items-center gap-2">
          {title}
          {!isCompleted && shownFiles.length > 0 && (
            <span className="text-xs opacity-75">
              ({shownFiles.length} {shownFiles.length === 1 ? "file" : "files"})
            </span>
          )}
        </span>
        <span className="flex items-center gap-2">
          {showRetryButton && onRetryFailed && (
            <button
              onClick={onRetryFailed}
              className="p-1 hover:bg-white/20 rounded transition-colors"
              title="Retry failed uploads"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          )}
          <ChevronDown
            className={clsx(
              "w-4 h-4 cursor-pointer transition-transform duration-300",
              collapsed && "rotate-180"
            )}
            onClick={() => setCollapsed(!collapsed)}
          />
          {isCompleted && !onCloseTray && (
            <X className="w-4 h-4 cursor-pointer" onClick={closeTray} />
          )}
          {onCloseTray && (
            <button
              onClick={onCloseTray}
              className="hover:bg-white/20 rounded p-0.5"
              title="Close tray"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </span>
      </div>

      {!collapsed && isCompleted && placeholder}
      {!collapsed && !isCompleted && (
        <div className="flex flex-col">
          <div className="px-2 text-sm">
            {currentFiles.map((file) => (
              <div
                key={file.tempId || file._id}
                className="flex items-center border p-2 rounded-md my-2 shadow-md"
              >
                <FileTrayItem
                  file={file}
                  remove={removeFile ? () => removeFile(file) : undefined}
                />
              </div>
            ))}
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-between px-2 py-2 border-t bg-gray-50">
              <button
                onClick={goToPrevPage}
                disabled={currentPage === 1}
                className={clsx(
                  "flex items-center gap-1 px-2 py-1 rounded text-xs",
                  currentPage === 1
                    ? "text-gray-400 cursor-not-allowed"
                    : "text-gray-600 hover:bg-gray-200 cursor-pointer"
                )}
              >
                <ChevronLeft className="w-3 h-3" />
                Previous
              </button>

              <span className="text-xs text-gray-600">
                Page {currentPage} of {totalPages}
              </span>

              <button
                onClick={goToNextPage}
                disabled={currentPage === totalPages}
                className={clsx(
                  "flex items-center gap-1 px-2 py-1 rounded text-xs",
                  currentPage === totalPages
                    ? "text-gray-400 cursor-not-allowed"
                    : "text-gray-600 hover:bg-gray-200 cursor-pointer"
                )}
              >
                Next
                <ChevronRight className="w-3 h-3" />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const calculateProgress = (files: FileWithStatus[]): number => {
  const doneFiles = files.filter(
    (file) => file.status === "completed" || file.status === "duplicate"
  ).length;
  if (files.length === 0) return 1.0;

  return doneFiles / files.length;
};

const FileTrayItem = ({
  file,
  remove,
}: {
  file: FileWithStatus;
  remove?: () => void;
}) => {
  if (file.status === "pending" || file.status === "uploading") {
    const progress = file.progress ?? 0;
    const pct = Math.round(progress * 100);

    let speedText = "";
    let etaText = "";
    if (
      file.status === "uploading" &&
      file.uploadStartedAt &&
      file.totalBytes &&
      progress > 0
    ) {
      const elapsedMs = Date.now() - file.uploadStartedAt;
      const elapsedSec = elapsedMs / 1000;
      if (elapsedSec > 0) {
        const uploadedBytes = progress * file.totalBytes;
        const mbPerSec = uploadedBytes / (1024 * 1024) / elapsedSec;
        speedText = `${mbPerSec.toFixed(1)} MB/s`;

        if (progress < 1) {
          const remainingBytes = file.totalBytes - uploadedBytes;
          const bytesPerSec = uploadedBytes / elapsedSec;
          const remainingSec = remainingBytes / bytesPerSec;
          if (remainingSec < 60) {
            etaText = `${Math.ceil(remainingSec)}s left`;
          } else {
            etaText = `${Math.ceil(remainingSec / 60)}m left`;
          }
        }
      }
    }

    return (
      <div className="flex flex-col gap-1 w-full">
        <div className="flex items-center gap-2">
          <LoaderCircle className="w-3 h-3 animate-spin text-blue-700" />
          <span className="truncate">{file.name}</span>
          {file.status === "uploading" && (
            <span className="text-xs text-gray-500 ml-auto shrink-0">
              {pct}%{speedText && ` · ${speedText}`}
              {etaText && ` · ${etaText}`}
            </span>
          )}
        </div>
        {file.status === "uploading" && (
          <div className="w-full bg-gray-200 rounded-full h-1">
            <div
              className="bg-blue-600 h-1 rounded-full transition-all duration-300"
              style={{ width: `${pct}%` }}
            />
          </div>
        )}
      </div>
    );
  }
  if (file.duplicate || file.status === "duplicate") {
    return (
      <div className="flex justify-between w-full">
        <div>
          <div>{file.name}</div>
          <div className="text-amber-600 flex items-center gap-2">
            <Copy className="w-3 h-3" />
            Duplicate — file already exists
          </div>
        </div>
      </div>
    );
  }
  if (file.status === "completed") {
    return (
      <div className="flex items-center gap-2">
        <CheckCircle className="w-3 h-3 text-green-700" />
        {file.name}
      </div>
    );
  }
  if (file.locked) {
    // text-amber-600
    return (
      <div className="flex justify-between w-full">
        <div>
          <div>{file.name}</div>
          <div className="text-amber-600 flex items-center gap-2">
            <LockKeyhole className="w-3 h-3" />
            This file needs to be unlocked
          </div>
        </div>
      </div>
    );
  }
  if (file.error || file.status === "failed") {
    return (
      <div className="flex justify-between w-full">
        <div>
          <div className="text-gray-800">{file.name}</div>
          <div className="flex items-center gap-2 text-red-500 text-sm">
            <OctagonX className="w-3 h-3" />
            {file.error || "Upload failed"}
          </div>
        </div>
      </div>
    );
  }
  return <p>Unknown</p>;
};

export default TrayViewer;
